import type { TreeDocument, Category } from '../api';
import type { UserSettings } from '../settings';
import { formatDate } from '../settings';

export interface TreeGridColumn {
  key: string;
  labelKey: string;
  sortable: boolean;
  hiddenClass?: string;
  format(doc: TreeDocument, settings: UserSettings): string;
}

export const defaultColumns: TreeGridColumn[] = [
  {
    key: 'displayName',
    labelKey: 'pages.browse.col_name',
    sortable: false,
    format: (doc) => doc.displayName || doc.fileName,
  },
  {
    key: 'referenceDate',
    labelKey: 'pages.browse.col_reference_date',
    sortable: true,
    hiddenClass: 'hidden md:block',
    format: (doc, settings) => (doc.referenceDate ? formatDate(doc.referenceDate, settings) : '–'),
  },
  {
    key: 'uploadedAt',
    labelKey: 'pages.browse.col_uploaded_at',
    sortable: true,
    hiddenClass: 'hidden lg:block',
    format: (doc, settings) => formatDate(doc.uploadedAt, settings),
  },
];

// ---------------------------------------------------------------------------
// Spec 010 — resolveSubject (E3 fallback chain)
// ---------------------------------------------------------------------------

/**
 * Resolves the display value for the "subject" cell following the fallback chain
 * defined in Spec 010 E3: subject → documentType → displayName → fileName.
 * "Not empty" = non-blank after trim. Never returns ''.
 * Supports both TreeDocument (subject direct field) and flat Document (extractedData).
 */
export function resolveSubject(doc: {
  subject?: string;
  documentType?: string;
  displayName?: string;
  fileName?: string;
  extractedData?: { extractedValues?: Record<string, string> } | null;
}): string {
  // 1. Direct subject field (Tree path, pre-coalesced by BigQuery)
  const subjectDirect = doc.subject?.trim();
  if (subjectDirect) return subjectDirect;

  // 2. extractedData.subject (flat list path)
  const subjectExtracted = doc.extractedData?.extractedValues?.['subject']?.trim();
  if (subjectExtracted) return subjectExtracted;

  // 3. Direct documentType field
  const docTypeDirect = doc.documentType?.trim();
  if (docTypeDirect) return docTypeDirect;

  // 4. extractedData.documentType (flat list path)
  const docTypeExtracted = doc.extractedData?.extractedValues?.['documentType']?.trim();
  if (docTypeExtracted) return docTypeExtracted;

  // 5. displayName
  const displayName = doc.displayName?.trim();
  if (displayName) return displayName;

  // 6. fileName
  const fileName = doc.fileName?.trim();
  if (fileName) return fileName;

  // 7. En-dash placeholder (Spec 010 E7 — never return empty string)
  return '–';
}

// ---------------------------------------------------------------------------
// Spec 010 — Whitelist and column definitions (E2)
// ---------------------------------------------------------------------------

/** Allowed column keys (Spec 010 E2 whitelist). */
export const ALLOWED_COLUMN_KEYS = ['name', 'subject', 'referenceDate', 'uploadedAt'] as const;

/** Subject column definition (Spec 010 E7: responsive, truncated, with title). */
const subjectColumn: TreeGridColumn = {
  key: 'subject',
  labelKey: 'pages.browse.col_subject',
  sortable: false,
  hiddenClass: 'hidden md:block',
  format: (doc) => resolveSubject(doc),
};

/** Maps config key → TreeGridColumn definition. */
const COLUMN_BY_KEY: Record<string, TreeGridColumn> = {
  name: defaultColumns[0],
  subject: subjectColumn,
  referenceDate: defaultColumns[1],
  uploadedAt: defaultColumns[2],
};

// ---------------------------------------------------------------------------
// Spec 010 — resolveColumns (E2/E6/E7)
// ---------------------------------------------------------------------------

/**
 * Resolves the ordered column list for a category, applying Spec 010 E2/E6 rules:
 *  - Returns `defaultColumns` when category has no `listColumns` (E6 default fallback).
 *  - Unknown keys are ignored with a console.warn (E2 robustness).
 *  - `name` is always the first column, deduplicated (E2/E7).
 */
export function resolveColumns(category?: Pick<Category, 'listColumns'> | null): TreeGridColumn[] {
  if (!category?.listColumns || category.listColumns.length === 0) {
    return defaultColumns;
  }

  const listCols = category.listColumns;

  // E2: filter to whitelist, warn on unknown keys
  const filteredKeys = listCols.filter((key) => {
    if (!(ALLOWED_COLUMN_KEYS as readonly string[]).includes(key)) {
      console.warn(
        `[treeGridColumns] Unknown column key '${key}' in listColumns — ignored (Spec 010 E2)`,
      );
      return false;
    }
    return true;
  });

  // E2/E7: 'name' is always first and never duplicated
  const keyOrder = ['name', ...filteredKeys.filter((k) => k !== 'name')];

  // Map to column definitions, dropping any unmapped keys
  const columns = keyOrder
    .map((key) => COLUMN_BY_KEY[key])
    .filter((col): col is TreeGridColumn => col !== undefined);

  return columns;
}
